import React from 'react';
import PropTypes from 'prop-types';
import {
  compose,
  withHandlers,
  withState,
  withProps,
  lifecycle,
} from 'recompose';
import moment from 'moment-timezone';
import { Row, Col, Alert } from 'reactstrap';
import { connect } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faSave,
  faEyeSlash,
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { faMinusCircle } from '@fortawesome/pro-light-svg-icons';

import Modal from 'components/Modal';
import Button from 'components/Button';
import Fieldset from 'components/Form/Fieldset';
import './FormActions.scss';

const enhance = compose(
  connect,
  withState('showDeleteModal', 'toggleShowDelete', false),
  withState('showSaveModal', 'toggleShowSaveModal', false),
  withHandlers({
    handleDelete: ({
      handleDelete,
      formik: {
        values: { id },
      },
    }) => () => handleDelete(id),
    initDelete: ({ toggleShowDelete }) => () => toggleShowDelete(true),
    cancelDelete: ({ toggleShowDelete }) => () => toggleShowDelete(false),
    initSave: ({ toggleShowSaveModal }) => event => {
      if (event) {
        event.preventDefault();
      }
      toggleShowSaveModal(true);
    },
    cancelSave: ({ toggleShowSaveModal }) => () => toggleShowSaveModal(false),
    confirmSave: ({ toggleShowSaveModal, handleSubmit, formik }) => () => {
      toggleShowSaveModal(false);
      if (handleSubmit) {
        handleSubmit();
      } else {
        formik.submitForm();
      }
    },
    confirmSecondarySave: ({
      toggleShowSaveModal,
      saveConfirmation,
      handleChangeStatus,
      handleSubmit,
      formik,
    }) => async () => {
      toggleShowSaveModal(false);

      if (
        saveConfirmation &&
        typeof saveConfirmation.onSecondaryConfirm === 'function'
      ) {
        await saveConfirmation.onSecondaryConfirm({ formik });
      } else if (saveConfirmation && saveConfirmation.secondaryConfirmStatus) {
        handleChangeStatus(saveConfirmation.secondaryConfirmStatus);
        return;
      }

      if (handleSubmit) {
        handleSubmit();
      } else {
        formik.submitForm();
      }
    },
  }),
  withProps(props => {
    if (props.lastSaved) {
      return null;
    }

    const {
      formik: {
        values: { lastModificationTime, creationTime },
      },
    } = props;

    return {
      lastSaved: lastModificationTime || creationTime,
    };
  }),
  withProps(props => {
    const {
      formik: { status },
    } = props;

    if (status) {
      return {
        changingPublishStatus: status.changingPublishStatus,
      };
    }

    return { changingPublishStatus: false };
  }),
  lifecycle({
    componentWillReceiveProps(nextProps) {
      if (this.props.isSubmitting && !nextProps.isSubmitting) {
        if (
          this.props.changingPublishStatus &&
          nextProps.changingPublishStatus
        ) {
          nextProps.formik.setStatus({
            ...nextProps.formik.status,
            changingPublishStatus: undefined,
          });
        }
      }
    },
  })
);

const statusString = {
  New: 'Not yet saved',
  Imported: 'Imported',
  Draft: 'Draft',
  Published: 'Published',
  Deleted: 'Deleted',
  get(key) {
    return this[key || 'New'];
  },
};

const statusColor = {
  New: 'warning',
  Imported: 'info',
  Draft: 'info',
  Published: 'success',
  Deleted: 'danger',
  get(key) {
    return this[key || 'New'];
  },
};

const flattenMessages = errors => {
  if (!errors) {
    return [];
  }

  if (typeof errors === 'string') {
    return [errors];
  }

  if (Array.isArray(errors)) {
    return errors.reduce(
      (result, error) => [...result, ...flattenMessages(error)],
      []
    );
  }

  if (typeof errors === 'object') {
    return Object.values(errors).reduce(
      (result, error) => [...result, ...flattenMessages(error)],
      []
    );
  }

  return [];
};

const uniqueMessages = errors => [...new Set(flattenMessages(errors))];

class LastSavedDisplay extends React.Component {
  static propTypes = {
    lastSaved: PropTypes.string,
  };

  static defaultProps = {
    lastSaved: null,
  };

  constructor(props) {
    super(props);
    this.ticker = setInterval(
      () =>
        this.setState({
          now: new Date(),
        }),
      1000
    );

    this.state = {
      now: new Date(),
    };
  }

  componentWillUnmount() {
    clearInterval(this.ticker);
  }

  render() {
    const { lastSaved } = this.props;
    const { now } = this.state;

    return <p>Last saved {moment(lastSaved).from(now)}</p>;
  }
}

const FormActions = ({
  initDelete,
  isComplete,
  isPublishing,
  isSubmitting,
  lastSaved,
  showPublishing,
  status,
  handleDelete,
  handleChangeStatus,
  formik: {
    dirty,
    values: { id },
  },
  showDeleteModal,
  toggleShowDelete,
  cancelDelete,
  showSaveModal,
  toggleShowSaveModal,
  initSave,
  cancelSave,
  confirmSave,
  confirmSecondarySave,
  displayName,
  changingPublishStatus,
  shouldHideDraft,
  saveConfirmation,
  publishValidationErrors,
}) => {
  const formActionsRef = React.useRef(null);
  const publishValidationMessages = uniqueMessages(publishValidationErrors);

  React.useLayoutEffect(() => {
    const rootNode = formActionsRef.current;
    if (!rootNode) {
      return undefined;
    }

    const form = rootNode.closest('form');
    if (!form) {
      return undefined;
    }

    const handleSubmitCapture = event => {
      if (!saveConfirmation || event.defaultPrevented) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (isSubmitting || isPublishing || changingPublishStatus || !dirty) {
        return;
      }

      if (!showSaveModal) {
        initSave(event);
      }
    };

    form.addEventListener('submit', handleSubmitCapture, true);

    return () => {
      form.removeEventListener('submit', handleSubmitCapture, true);
    };
  }, [
    saveConfirmation,
    showSaveModal,
    initSave,
    dirty,
    isSubmitting,
    isPublishing,
    changingPublishStatus,
  ]);

  return (
    <div className="form-actions" ref={formActionsRef}>
      <div className="action-group">
        <Row>
          <Col xs={{ size: '', order: 2 }}>
            <Button
              block
              className="btn-save-changes"
              color="primary"
              disabled={!dirty || isPublishing || changingPublishStatus}
              icon={
                showPublishing ? (
                  <FontAwesomeIcon icon={faSave} />
                ) : (
                  <FontAwesomeIcon icon={faCheck} />
                )
              }
              loading={isSubmitting && !changingPublishStatus}
              type="submit"
              onClick={saveConfirmation ? initSave : undefined}
            >
              {showPublishing ? 'Save Changes' : 'Save'}
            </Button>
          </Col>
          {!showPublishing && id && handleDelete && (
            <Col xs={{ size: 'auto', order: 1 }}>
              <Button
                block
                buttonStyle="outline"
                color="danger"
                disabled={isSubmitting || changingPublishStatus}
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                onClick={initDelete}
                type="button"
              >
                Delete
              </Button>
            </Col>
          )}
        </Row>
        {lastSaved && <LastSavedDisplay lastSaved={lastSaved} />}
      </div>
      {showPublishing && (
        <div className="action-group">
          <Fieldset legend="Publishing">
            <p>
              Status:{' '}
              <strong className={`text-${statusColor.get(status)}`}>
                {statusString.get(status)}
              </strong>
            </p>
            <Row className="align-items-center">
              {status !== 'Deleted' && handleDelete && (
                <Col xs={6}>
                  <Button
                    block
                    buttonStyle="outline"
                    color="danger"
                    disabled={
                      status === 'New' || isSubmitting || changingPublishStatus
                    }
                    icon={<FontAwesomeIcon icon={faTrashAlt} />}
                    onClick={initDelete}
                    size="sm"
                    type="button"
                  >
                    Delete
                  </Button>
                </Col>
              )}
              <Col xs={6}>
                {(status === 'Published' || status === 'Deleted') &&
                !shouldHideDraft ? (
                  <Button
                    block
                    color="warning"
                    disabled={isSubmitting || changingPublishStatus}
                    icon={<FontAwesomeIcon icon={faEyeSlash} />}
                    loading={changingPublishStatus}
                    onClick={() => handleChangeStatus('Draft')}
                    size="sm"
                    type="button"
                  >
                    Make Draft
                  </Button>
                ) : null}
                {status !== 'Published' ? (
                  <Button
                    block
                    color="success"
                    disabled={
                      !isComplete || isSubmitting || changingPublishStatus
                    }
                    icon={<FontAwesomeIcon icon={faCheck} />}
                    loading={changingPublishStatus}
                    onClick={() => handleChangeStatus('Published')}
                    size="sm"
                    type="button"
                  >
                    Publish Now
                  </Button>
                ) : null}
              </Col>
            </Row>
            {publishValidationMessages.length > 0 && (
              <Alert color="danger" className="publish-validation-alert">
                <strong>Review the form.</strong>
              </Alert>
            )}
          </Fieldset>
        </div>
      )}
      {showDeleteModal && (
        <Modal
          hideButton
          show={showDeleteModal}
          color="danger"
          confirmationModal
          size="sm"
          onToggle={toggleShowDelete}
          footer={
            <Row>
              <Col xs="auto">
                <Button color="dark" outline onClick={cancelDelete}>
                  <FontAwesomeIcon icon={faMinusCircle} /> Cancel
                </Button>
              </Col>
              <Col xs="auto">
                <Button color="danger" onClick={handleDelete}>
                  <FontAwesomeIcon icon={faTrashAlt} /> Yes, Delete
                </Button>
              </Col>
            </Row>
          }
        >
          <h3>Are you sure you want to delete this {displayName}?</h3>
        </Modal>
      )}
      {showSaveModal && saveConfirmation && (
        <Modal
          hideButton
          show={showSaveModal}
          color={saveConfirmation.color || 'warning'}
          confirmationModal
          size="sm"
          onToggle={toggleShowSaveModal}
          footer={
            <Row>
              {!saveConfirmation.hideCancel && (
                <Col xs="auto">
                  <Button color="dark" outline onClick={cancelSave}>
                    {saveConfirmation.cancelIcon !== null && (
                      <React.Fragment>
                        <FontAwesomeIcon
                          icon={saveConfirmation.cancelIcon || faMinusCircle}
                        />{' '}
                      </React.Fragment>
                    )}
                    {saveConfirmation.cancelLabel || 'Cancel'}
                  </Button>
                </Col>
              )}
              {saveConfirmation.secondaryConfirmLabel ? (
                <React.Fragment>
                  <Col xs={12} className="mt-2">
                    <Button block color="primary" onClick={confirmSave}>
                      {saveConfirmation.confirmLabel || 'Save'}
                    </Button>
                  </Col>
                  <Col xs={12} className="mt-2">
                    <Button
                      block
                      color="primary"
                      buttonStyle="outline"
                      onClick={confirmSecondarySave}
                    >
                      {saveConfirmation.secondaryConfirmLabel}
                    </Button>
                  </Col>
                </React.Fragment>
              ) : (
                <Col xs="auto">
                  <Button color="primary" onClick={confirmSave}>
                    <FontAwesomeIcon icon={faSave} />{' '}
                    {saveConfirmation.confirmLabel || 'Save'}
                  </Button>
                </Col>
              )}
            </Row>
          }
        >
          <h3>{saveConfirmation.title}</h3>
          {saveConfirmation.body &&
            (typeof saveConfirmation.body === 'string' ? (
              <p>{saveConfirmation.body}</p>
            ) : (
              saveConfirmation.body
            ))}
        </Modal>
      )}
    </div>
  );
};

FormActions.propTypes = {
  cancelDelete: PropTypes.func,
  handleChangeStatus: PropTypes.func,
  handleDelete: PropTypes.func,
  initDelete: PropTypes.func,
  initSave: PropTypes.func,
  displayName: PropTypes.string,
  formik: PropTypes.shape({
    dirty: PropTypes.bool.isRequired,
  }).isRequired,
  isComplete: PropTypes.bool, // Data can be published (eg. movie has all required info to go live)
  isPublishing: PropTypes.bool, // Loading state on the publish button
  isSubmitting: PropTypes.bool, // Loading state on the save button (not sure if this is a good name for the prop)
  lastSaved: PropTypes.string, // Date/time that can be passed to moment.js
  saveConfirmation: PropTypes.shape({
    title: PropTypes.string.isRequired,
    body: PropTypes.node,
    confirmLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    cancelIcon: PropTypes.object, // FontAwesome icon or null to hide
    color: PropTypes.string,
    hideCancel: PropTypes.bool,
    secondaryConfirmLabel: PropTypes.string,
    secondaryConfirmStatus: PropTypes.string,
    onSecondaryConfirm: PropTypes.func,
  }),
  publishValidationErrors: PropTypes.shape({}),
  showDeleteModal: PropTypes.bool,
  showSaveModal: PropTypes.bool,
  showPublishing: PropTypes.bool, // Shows the publishing actions
  status: PropTypes.oneOf(Object.keys(statusString)),
  changingPublishStatus: PropTypes.bool,
  shouldHideDraft: PropTypes.bool,
  toggleShowDelete: PropTypes.func,
  toggleShowSaveModal: PropTypes.func,
  cancelSave: PropTypes.func,
  confirmSave: PropTypes.func,
  confirmSecondarySave: PropTypes.func,
};

FormActions.defaultProps = {
  cancelDelete: () => {},
  displayName: 'entity',
  handleChangeStatus: () => false,
  handleDelete: null,
  initDelete: () => {},
  initSave: () => {},
  isComplete: false,
  isPublishing: false,
  isSubmitting: false,
  lastSaved: '',
  saveConfirmation: null,
  publishValidationErrors: {},
  showDeleteModal: true,
  showSaveModal: false,
  showPublishing: false,
  status: 'New',
  changingPublishStatus: false,
  shouldHideDraft: false,
  toggleShowDelete: null,
  toggleShowSaveModal: null,
  cancelSave: () => {},
  confirmSave: () => {},
  confirmSecondarySave: () => {},
};

export default enhance(FormActions);
